/**
 * Shared visual shell for post-payment membership portal screens
 * (/portal, /pending-approval, /portal/complete-profile).
 *
 * Mobile PWA: full-viewport app chrome, safe-area, optional sticky bottom dock.
 */

import { cn } from "@/lib/utils";
import { useEffect, type ReactNode } from "react";

type MembershipPortalShellProps = {
  children: ReactNode;
  className?: string;
  /** Max width of the centered column */
  maxWidthClassName?: string;
  /** Optional top badge / status strip above the main card */
  topSlot?: ReactNode;
  /** Optional footer under the card (scrolls with content) */
  footer?: ReactNode;
  /**
   * Sticky bottom action bar (PWA-style). Stays above the home indicator.
   * Content area gets extra bottom padding so CTAs never cover copy.
   */
  bottomDock?: ReactNode;
};

export function MembershipPortalShell({
  children,
  className,
  maxWidthClassName = "max-w-md",
  topSlot,
  footer,
  bottomDock,
}: MembershipPortalShellProps) {
  // PWA body chrome: dark status bar + lock overscroll bounce on this surface
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    const prevBg = document.body.style.backgroundColor;
    document.body.classList.add("portal-pwa-active");
    document.body.style.backgroundColor = "#09090b";
    return () => {
      document.body.classList.remove("portal-pwa-active");
      document.body.style.overflow = prevOverflow;
      document.body.style.backgroundColor = prevBg;
    };
  }, []);

  return (
    <div
      className={cn(
        "portal-app-shell relative flex min-h-[100dvh] min-h-[100svh] flex-col bg-zinc-950 text-zinc-100",
        "overflow-x-hidden overflow-y-auto overscroll-y-contain touch-pan-y",
        // Safe areas: notch + home indicator
        "pt-[max(0.75rem,env(safe-area-inset-top))]",
        bottomDock
          ? "pb-0"
          : "pb-[max(1.5rem,env(safe-area-inset-bottom))]",
        className,
      )}
    >
      <div className="portal-ambient" aria-hidden />

      <div
        className={cn(
          "relative z-[1] mx-auto flex w-full flex-1 flex-col px-4",
          maxWidthClassName,
          // Room for sticky dock so last content isn't hidden
          bottomDock
            ? "pb-[calc(7.5rem+env(safe-area-inset-bottom))] pt-3 sm:pb-[calc(8rem+env(safe-area-inset-bottom))] sm:pt-6"
            : "items-center justify-center py-8 sm:py-12",
        )}
      >
        <div className={cn("w-full space-y-3 sm:space-y-4", !bottomDock && "my-auto")}>
          {topSlot}
          {children}
          {footer}
        </div>
      </div>

      {bottomDock ? (
        <div className="portal-bottom-dock" role="toolbar" aria-label="Primary actions">
          <div className={cn("portal-bottom-dock-inner mx-auto w-full", maxWidthClassName)}>
            {bottomDock}
          </div>
        </div>
      ) : null}
    </div>
  );
}

type MembershipPortalCardProps = {
  children: ReactNode;
  className?: string;
  tone?: "default" | "success" | "warning";
};

export function MembershipPortalCard({
  children,
  className,
  tone = "default",
}: MembershipPortalCardProps) {
  return (
    <div
      className={cn(
        "portal-card",
        tone === "success" && "border-emerald-500/25 shadow-emerald-950/20",
        tone === "warning" && "border-amber-500/25 shadow-amber-950/20",
        className,
      )}
    >
      {children}
    </div>
  );
}

type MembershipPortalIconBadgeProps = {
  children: ReactNode;
  tone?: "amber" | "emerald" | "sky" | "zinc";
  className?: string;
};

export function MembershipPortalIconBadge({
  children,
  tone = "amber",
  className,
}: MembershipPortalIconBadgeProps) {
  const toneClass =
    tone === "emerald"
      ? "border-emerald-300/25 bg-emerald-500/15 text-emerald-300"
      : tone === "sky"
        ? "border-sky-300/25 bg-sky-500/15 text-sky-300"
        : tone === "zinc"
          ? "border-white/10 bg-white/[0.06] text-zinc-200"
          : "border-amber-300/25 bg-amber-500/15 text-amber-300";

  return (
    <div
      className={cn(
        "mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border shadow-inner backdrop-blur",
        toneClass,
        className,
      )}
    >
      {children}
    </div>
  );
}
